/**
 * The design-area vocabulary, shared by every screen that offers, sends or
 * displays one.
 *
 * Three different backend fields hold one of these strings:
 *   - `design_areas`            string[]  proof template  (/catalog/templates)
 *   - `design_area`             string    design product template (/product-templates)
 *   - `configured_design_area`  string    design request  (/designs)
 *
 * The backend normalizes case, spaces, hyphens and underscores, so "Left Sleeve"
 * resolves the same as "left_sleeve". Always write the canonical key anyway so
 * stored data stays consistent.
 */

export type DesignAreaOption = {
  key: string;
  label: string;
};

/** Areas that actually get printed on the blank. */
export const PRINTED_DESIGN_AREAS: DesignAreaOption[] = [
  { key: "front", label: "Front" },
  { key: "back", label: "Back" },
  { key: "pocket", label: "Pocket" },
  { key: "left_sleeve", label: "Left Sleeve" },
  { key: "right_sleeve", label: "Right Sleeve" },
  { key: "welts_and_bottom_cuff", label: "Welts and Bottom Cuff" },
  // Front Pocket is the kangaroo pocket on a hoodie, NOT the 3"x3" left-chest
  // Pocket above — different sizes, different placements.
  { key: "hood", label: "Hood" },
  { key: "front_pocket", label: "Front Pocket" },
  // The rest of the hood areas on the Printful all-over-print garments. Inner
  // Hood is the inside face of an AOP_SWU_Hoodie's hood and Facing the band
  // around its opening; Left/Right Hood are the two outer panels on an
  // AOP_windbreaker. All are separate prints from the Hood shell above, and a
  // garment can carry several at once.
  { key: "inner_hood", label: "Inner Hood" },
  { key: "facing", label: "Facing" },
  { key: "left_hood", label: "Left Hood" },
  { key: "right_hood", label: "Right Hood" },
];

/**
 * Storefront photography, NOT prints. Nothing here reaches the printer or the
 * fulfillment order. The backend picks the Shopify photography like this:
 *
 *   proof_combo (+ either other)  main image and every variant are the combo;
 *                                 gallery is combo, then front, then back
 *   proof_front AND proof_back    main image is proof_front; gallery front, back
 *   one of front/back, no combo   ignored entirely, printed areas are used
 *   no renders                    printed areas are used
 *
 * Listed combo first, matching the gallery order.
 */
export const STOREFRONT_RENDER_AREAS: DesignAreaOption[] = [
  { key: "proof_combo", label: "Proof Combo" },
  { key: "proof_front", label: "Proof Front" },
  { key: "proof_back", label: "Proof Back" },
];

/**
 * The backend matches this one loosely — "neck", "neck label", "label" and "tag"
 * all resolve to it — but "neck label" is the key to write.
 */
export const NECK_LABEL_AREA: DesignAreaOption = {
  key: "neck label",
  label: "Neck Label",
};

const NECK_LABEL_ALIASES = ["neck", "neck_label", "label", "tag"];

/**
 * Same normalization the backend applies: case, spaces, hyphens and underscores
 * are all equivalent. Use this for comparisons, never for what gets stored.
 */
export function canonicalizeDesignArea(area: string): string {
  return (area || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** The known option a stored string resolves to, or null when it is free text. */
export function resolveDesignArea(area: string): DesignAreaOption | null {
  const canonical = canonicalizeDesignArea(area);
  if (!canonical) {
    return null;
  }
  if (NECK_LABEL_ALIASES.includes(canonical)) {
    return NECK_LABEL_AREA;
  }
  const known = [...PRINTED_DESIGN_AREAS, ...STOREFRONT_RENDER_AREAS].find(
    (x) => canonicalizeDesignArea(x.key) === canonical
  );
  return known ?? null;
}

/**
 * How an area is shown to a person. Unrecognized areas are kept and title-cased,
 * the same way fulfillment renders them.
 */
export function designAreaLabel(area: string): string {
  const known = resolveDesignArea(area);
  if (known) {
    return known.label;
  }
  return (area || "")
    .trim()
    .split(/[\s\-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const FREE_TEXT_RANK = 100;
const NECK_LABEL_RANK = 1000;

function designAreaRank(area: string): number {
  const canonical = canonicalizeDesignArea(area);
  if (NECK_LABEL_ALIASES.includes(canonical)) {
    return NECK_LABEL_RANK;
  }
  const printed = PRINTED_DESIGN_AREAS.findIndex(
    (x) => canonicalizeDesignArea(x.key) === canonical
  );
  if (printed !== -1) {
    return printed;
  }
  const render = STOREFRONT_RENDER_AREAS.findIndex(
    (x) => canonicalizeDesignArea(x.key) === canonical
  );
  if (render !== -1) {
    return PRINTED_DESIGN_AREAS.length + render;
  }
  return FREE_TEXT_RANK;
}

/**
 * Display order, wherever areas are listed or variants are grouped:
 * front → back → pocket → left sleeve → right sleeve → welts and bottom cuff →
 * storefront renders → free text → neck label last. Free-text areas keep the
 * order they were given in.
 */
export function sortDesignAreas(areas: string[]): string[] {
  return [...areas].sort((a, b) => designAreaRank(a) - designAreaRank(b));
}

/**
 * A bare "sleeve" does not say which arm. The backend refuses to guess rather
 * than print on the wrong sleeve with no error anywhere, so neither do we.
 */
export function isBareSleeve(area: string): boolean {
  return canonicalizeDesignArea(area) === "sleeve";
}
